import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Product } from "./data";

type Item = { product: Product; qty: number };
type Ctx = {
  items: Item[];
  add: (p: Product) => void;
  remove: (id: string) => void;
  setQty: (id: string, qty: number) => void;
  clear: () => void;
  total: number;
  count: number;
};

const CartCtx = createContext<Ctx | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("kupish:cart");
      if (raw) setItems(JSON.parse(raw));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem("kupish:cart", JSON.stringify(items));
  }, [items]);

  const add = (p: Product) =>
    setItems((cur) => {
      const i = cur.find((x) => x.product.id === p.id);
      return i
        ? cur.map((x) => (x.product.id === p.id ? { ...x, qty: x.qty + 1 } : x))
        : [...cur, { product: p, qty: 1 }];
    });
  const remove = (id: string) => setItems((c) => c.filter((x) => x.product.id !== id));
  const setQty = (id: string, qty: number) =>
    setItems((c) => c.map((x) => (x.product.id === id ? { ...x, qty: Math.max(1, qty) } : x)));
  const clear = () => setItems([]);
  const total = items.reduce((s, i) => s + i.product.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartCtx.Provider value={{ items, add, remove, setQty, clear, total, count }}>
      {children}
    </CartCtx.Provider>
  );
}

export function useCart() {
  const c = useContext(CartCtx);
  if (!c) throw new Error("useCart must be used within CartProvider");
  return c;
}
